<script setup lang="ts">
import {
    faArrowLeft,
    faArrowRight,
    faBasketShopping,
    faBuildingCircleExclamation,
    faCar,
    faCartCirclePlus,
    faCheckDouble,
    faCreditCard,
    faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import PaymentLink from './_components/PaymentLink.vue';
import ReserveCardInfo from '@components/layout/reserve/ReserveCardInfo.vue';
import FormSidebar from '@components/layout/reserve/FormSidebar.vue';
import ReserveStepper, {
    type StepperStep,
} from '@components/layout/reserve/ReserveStepper.vue';
import AutoReserveCardInfo from '@components/layout/reserve/AutoReserveCardInfo.vue';
import ClubReservePoint from '@components/layout/reserve/ClubReservePoint.vue';
import TermsAndConditionsLink from '@components/ui/prepay&terms-and-conditions/TermsAndConditionLink.vue';
import SafarmarketAlert from '@components/ui/hotelPage&reserve/SafarmarketAlert.vue';
import FooterButtons from '@components/util/FooterButtons.vue';
import ReserveBill from '@components/ui/hotelPage&reserve&view/reserve-bill/ReserveBill.vue';
import type {
    MrBillItem,
    MrBillItemsGroup,
} from '@components/ui/hotelPage&reserve&view/reserve-bill/types';
import useBillEntries from '@composable/useBillEntries';
import { NuxtPage } from '#components';
import SiteHeader from '@components/layout/site-header';
import SiteFooter from '@components/layout/site-footer';

interface ReservePageProps {
    submit: () => void;
    submitTitle: string;
    backTitle?: string;
    disabled?: boolean;
    loading?: boolean;
}

const { isMrbilit } = useWhiteLabel();
const layoutStore = useLayoutStore();
const isFooterCompact = computed(() => !layoutStore.isRedirectFromSafarmarket);
const authStore = useAuthStore();
const { loggedIn } = classToRefs(authStore);
const formStore = useFormStore();
const { packagesCancellationTerms } = classToRefs(formStore);
const autoReserveStore = useAutoReserveStore();
const invoiceStore = useInvoiceStore();
const { paymentLinkInProgress } = classToRefs(invoiceStore);
const packageSelectStore = usePackageSelectStore();
const { selectedPackages, billMessages } = classToRefs(packageSelectStore);
const route = useRoute();
const billComponent = ref<InstanceType<typeof ReserveBill>>();
const page = ref<{ pageRef: ReservePageProps }>();
const router = useRouter();
const isMounted = useMounted();
const logoutClass = 'hide-logout';

useHead({
    htmlAttrs: {
        class: logoutClass,
    },
});

onUnmounted(() => document.documentElement.classList.remove(logoutClass));

onBeforeRouteLeave((to, from, next) => {
    if (to.path === from.path) {
        next();
        return;
    }
    if (!to.path.startsWith('/reserve') && !from.path.includes('/form')) {
        if (isAutoReserve.value) {
            autoReserveStore.resetAutoReserve(true);
        }
    }
    next();
});
const { isMobile } = useSize();
const {
    billDiscounts,
    billEntries,
    billLoading,
    preBillDiscounts,
    preBillEntries,
    preBillLoading,
} = useBillEntries();
const initialDetailAccordionState = computed(() =>
    currentPage.value === 'form' ||
    packageSelectStore.selectedPackages.some(pkg => pkg.isHotelPackage())
        ? 'closed'
        : 'opened'
);
const termsAndConditionShow = computed(() => currentPage.value === 'prepay');
const initialBillAccordionState = computed(() =>
    currentPage.value === 'raise' ? 'closed' : 'opened'
);
const isAutoReserve = computed(
    () => autoReserveStore.isAutoReserve || currentPage.value === 'auto'
);
const showScore = computed(
    () => loggedIn.value && !['raise', 'auto'].includes(currentPage.value)
);
const currentPage = computed(() => {
    if (route.path.includes('/reserve/raise')) {
        return 'raise';
    }
    if (route.path.includes('/reserve/form')) {
        return 'form';
    }
    if (route.path.includes('/reserve/prepay')) {
        return 'prepay';
    }
    if (route.path.includes('/reserve/auto')) {
        return 'auto';
    }
    return 'floating';
});

const isTrainCarReserve = computed(() => packageSelectStore.hasCarSelect);
const isTrainReserve = computed(() =>
    packageSelectStore.selectedPackages.some(pkg => pkg.isTrainPackage())
);

const stepperSteps = computed<
    (StepperStep & {
        page: string;
    })[]
>(() =>
    [
        {
            page: 'raise',
            icon: faCartCirclePlus,
            title: 'ارتقاء سفر',
            condition:
                isMounted.value &&
                packageSelectStore.hasRaisePage &&
                !isAutoReserve.value,
        },
        {
            page: 'auto',
            icon: faBasketShopping,
            title: 'سبد خرید',
            condition: !isMounted.value || isAutoReserve.value,
        },
        {
            page: 'form',
            icon: faUserGroup,
            title: 'مسافران',
            condition: !isTrainCarReserve.value,
        },
        {
            page: 'form',
            icon: faCar,
            title: 'خودرو و مالک',
            condition: isTrainCarReserve.value,
        },
        {
            page: 'floating',
            icon: faBuildingCircleExclamation,
            title: 'بررسی ظرفیت',
            condition: isMounted.value && isFloating.value,
        },
        {
            page: 'prepay',
            icon: faCheckDouble,
            title: 'تأیید اطلاعات',
        },
        {
            page: 'pay',
            icon: faCreditCard,
            title: 'پرداخت',
            condition: isMounted.value && autoReserveStore.paymentAmount !== 0,
        },
    ].filter(item => item.condition !== false)
);
const currentStep = computed({
    get(): number {
        return stepperSteps.value.findIndex(
            step => step.page === currentPage.value
        );
    },
    set(newValue: number) {
        const prev = currentStep.value;
        for (let i = 0; i < prev - newValue; i++) {
            back();
        }
    },
});
const nextButtonText = computed(() => page.value?.pageRef?.submitTitle);

const isFloating = computed(() => packageSelectStore.currentPackageIsFloating);
const hideCredit = computed(() => {
    if (isAutoReserve.value) {
        return false;
    }
    return !invoiceStore.useCredit;
});
const backButtonText = computed(
    () => page.value?.pageRef?.backTitle || 'بازگشت'
);
const packageTerms = computed<(string | null)[]>(
    () =>
        selectedPackages.value?.map(pkg => {
            if (packagesCancellationTerms.value.has(pkg)) {
                return packagesCancellationTerms.value.get(pkg)!;
            } else {
                return pkg.termsAndConditions;
            }
        }) || []
);

watch(loggedIn, () => invoiceStore.loadReceivedTripScore());

const computedBillEntries = computed<MrBillItemsGroup[]>(() => {
    if (isAutoReserve.value) {
        return autoReserveStore.autoReserveBillEntries
            .filter(e => e.prices.length)
            .map(entry => ({
                title: entry.itineraryPackage?.invoiceTitle ?? '',
                items: entry.prices.map(price => ({
                    count: price.count,
                    title: price.title || '',
                    unitPrice: price.price,
                })),
            }));
    }
    if (currentPage.value === 'form' && isFloating.value) {
        return preBillEntries.value;
    }

    return currentPage.value === 'raise' || currentPage.value === 'floating'
        ? preBillEntries.value
        : billEntries.value;
});
const computedBillDiscounts = computed<MrBillItem[]>(() => {
    if (isAutoReserve.value && autoReserveStore.servicePriceDiscount) {
        return [
            {
                count: 1,
                title: 'تخفیف رزرو خودکار',
                unitPrice: autoReserveStore.servicePriceDiscount,
            },
        ];
    }
    if (currentPage.value === 'form' && isFloating.value) {
        return preBillDiscounts.value;
    }
    return currentPage.value === 'raise' || currentPage.value === 'floating'
        ? preBillDiscounts.value
        : billDiscounts.value;
});
const computedBillLoading = computed(() => {
    if (isAutoReserve.value) {
        return autoReserveStore.isLoadingPrices;
    }
    return currentPage.value === 'raise'
        ? preBillLoading.value
        : billLoading.value;
});
const loadingProceed = computed(() => page.value?.pageRef?.loading);
const stepperDisabled = computed(
    () => invoiceStore.isLoadFromSession || autoReserveStore.isLoadFromSession
);
const proceedDisabled = computed(() => !!page.value?.pageRef?.disabled);
const paymentLinkIsActive = computed(
    () =>
        currentPage.value === 'prepay' &&
        packageSelectStore.selectedPackages.some(
            pkg => pkg.isFlightPackage() && pkg.international
        ) &&
        !route.query?.token &&
        !isMobile.value
);
const back = () => router.back();
const proceed = () => page.value?.pageRef?.submit();
onMounted(() => {
    // prepay call auto reserve load session by itself
    // why layout should call this for pages ?
    if (!route.path.includes('/prepay')) {
        autoReserveStore.loadSession();
    }
});
</script>

<template>
    <SiteHeader
        disable-switch-account
        :main-account-only="isTrainReserve"
        show-back
        class="site-header"
        compact
    />
    <div class="reserves-pages-container">
        <FooterButtons
            mobile-only
            fixed
            class="fixed-proceed"
            has-cancel
            :cancel-button-name="backButtonText"
            :proceed-button-name="nextButtonText"
            :proceed-button-disabled="proceedDisabled"
            :proceed-button-loading="loadingProceed"
            @click:proceed="proceed"
            @click:cancel="back"
        >
            <template #prepend>
                <ReserveBill
                    :items="computedBillEntries"
                    :discounts="computedBillDiscounts"
                    :hide-credit
                    :messages="billMessages"
                    initial-state="closed"
                    :loading="computedBillLoading"
                />
                <TermsAndConditionsLink v-if="termsAndConditionShow" />
            </template>
        </FooterButtons>
        <div class="extra-header">
            <div class="stepper-wrapper">
                <ReserveStepper
                    v-model="currentStep"
                    :steps="stepperSteps"
                    :disabled="stepperDisabled"
                />
            </div>
        </div>
        <div class="page-container">
            <div class="page-sidebar">
                <SafarmarketAlert v-if="currentPage === 'form'" />
                <AutoReserveCardInfo v-if="isAutoReserve" />
                <template v-else>
                    <ReserveCardInfo
                        v-for="(pkg, packageIndex) in selectedPackages"
                        :key="packageIndex"
                        :package="pkg"
                        show-terms-and-conditions
                        :accordion-default="initialDetailAccordionState"
                        :package-terms="
                            packageTerms?.[packageIndex] || undefined
                        "
                    />
                </template>
                <ClubReservePoint v-show="showScore" desktop-only />
                <!-- <mr-flash-message
                    v-if="autoReserveWarning"
                    :message="autoReserveWarning"
                /> -->
                <mr-fix :offset-top="12" :offset-stop="32" :disabled="isMobile">
                    <div class="sticky-content">
                        <FormSidebar
                            class="desktop-bill"
                            @click="() => billComponent?.toggle()"
                        >
                            <ReserveBill
                                ref="billComponent"
                                :items="computedBillEntries"
                                :discounts="computedBillDiscounts"
                                :initial-state="initialBillAccordionState"
                                :hide-credit
                                :messages="billMessages"
                                :loading="computedBillLoading"
                            />
                        </FormSidebar>
                        <PaymentLink
                            v-if="paymentLinkIsActive"
                            v-model:loading="paymentLinkInProgress"
                            :disabled="!!loadingProceed"
                        />
                        <TermsAndConditionsLink
                            v-if="!isMobile && termsAndConditionShow"
                        />
                        <mr-button
                            full-width
                            :left-icon="faArrowLeft"
                            class="proceed-button"
                            :loading="loadingProceed || paymentLinkInProgress"
                            :disabled="proceedDisabled"
                            @click="proceed"
                        >
                            {{ nextButtonText }}
                        </mr-button>
                    </div>
                </mr-fix>
            </div>
            <div class="page-content">
                <ClientOnly>
                    <NuxtPage ref="page" />
                </ClientOnly>
                <mr-button
                    v-if="!route?.query?.token"
                    variant="transparent"
                    class="back-button"
                    :right-icon="faArrowRight"
                    @click="back"
                >
                    {{ backButtonText }}
                </mr-button>
                <ClubReservePoint v-show="showScore" mobile-only />
            </div>
        </div>
    </div>
    <SiteFooter :compact="isFooterCompact" class="site-footer desktop-only" />
    <VitePwaManifest v-if="isMrbilit" />
</template>
<style scoped lang="scss">
.reserves-pages-container {
    @include mobile-only {
        padding-bottom: 100px;
    }
}
.reserves-pages-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 80vh;
    .extra-header {
        width: 100%;
        background: usePalette();
        margin-bottom: -36px;
        height: 150px;
        padding-top: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        .stepper-wrapper {
            width: 700px;
            flex-shrink: 0;
            @include mobile-only {
                width: 100%;
                overflow-x: auto;
                @include no-scrollbar;
            }
        }
    }

    .page-container {
        display: flex;
        flex-direction: row-reverse;
        gap: 16px;
        width: 100%;
        max-width: $app-container-max-width;
        @include mobile-only {
            flex-direction: column;
            gap: 8px;
        }
        @include mobile-only($app-container-max-width + 32px) {
            padding: 0 12px;
        }

        .page-sidebar {
            display: flex;
            flex-direction: column;
            gap: 12px;
            flex: 0 0 27.5%;
            max-width: 27.5%;
            @include mobile-only {
                width: 100%;
                max-width: 100%;
                gap: 8px;
            }
            .sticky-content {
                display: flex;
                flex-direction: column;
                gap: 12px;
                .desktop-bill {
                    cursor: pointer;
                    @include mobile-only {
                        display: none;
                    }
                }
                .proceed-button {
                    @include mobile-only {
                        display: none;
                    }
                }
            }
        }

        .page-content {
            flex: 0 1 71.16%;
            width: 71.16%;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            @include mobile-only {
                gap: 8px;
                width: unset;
                margin-bottom: 50px;
            }
            .back-button {
                @include mobile-only {
                    display: none;
                }
            }
        }
    }
    .fixed-proceed {
        gap: 8px;
        position: fixed;
        bottom: 0;
        right: 0;
        padding: 12px;
        z-index: 1001001;
        @include shadow(2);
    }
}
</style>
